import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useOptionalSensitiveDataSession } from "@/lib/sensitive-data-session-provider";
import { saveSessionSecureFile } from "@/lib/save-session-secure-file";

/**
 * s227 (LOGOUT-SAVE-CHECK-1, ruled s27 Q4b) — SIGNING OUT WITH UNSAVED SECURE FILE CHANGES ASKS FIRST.
 *
 * Cat A values live only in this browser's memory until they are written to the Secure File (they never reach the
 * server). Signing out drops them. Q4b: the banner blocks no navigation, but sign-out is the moment data could be
 * lost, so it asks — Download / Log out anyway / Cancel.
 *
 * 🔴 EVERY sign-out path goes through `useGuardedSignOut` (the interview's Sign out, `SignOutButton`, the account
 * menus). A path that calls AuthContext's logout directly skips the check — that is the G8 hole this closes.
 *
 * Download writes the file (the banner's own save, `saveSessionSecureFile`) and does NOT sign out on its own: the
 * dialog then says the file is saved and offers Log out. Signing out straight after a browser download triggers a
 * full-page navigation that can race the download, so the planner makes that second click.
 *
 * With nothing unsaved — or on a surface with no Secure File session (advisor, administrator) — sign-out runs at
 * once, as before.
 */

type SignOut = () => void | Promise<void>;

const GuardContext = createContext<((signOut: SignOut) => void) | null>(null);

export const LOGOUT_SAVE_CHECK = {
  title: "Your secure file has unsaved changes",
  savedTitle: "Your secure file is saved",
  download: "Download",
  logOutAnyway: "Log out anyway",
  cancel: "Cancel",
  logOut: "Log out",
} as const;

export function LogoutSaveCheckProvider({ children }: { children: ReactNode }) {
  const session = useOptionalSensitiveDataSession();
  const [pending, setPending] = useState<SignOut | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  // Synchronous lock: a double click inside one tick must not save twice (D-169.10 — `disabled` is not a lock).
  const saving = useRef(false);

  const guard = useCallback((signOut: SignOut) => {
    if (session?.hasUnsavedChanges) {
      setSaved(false);
      setError(false);
      setPending(() => signOut);
      return;
    }
    void signOut();
  }, [session]);

  const close = () => { if (!saving.current) setPending(null); };

  // The values are cleared only AFTER the sign-out succeeds: a failed logout leaves the planner signed in, and their
  // unsaved values must still be there. (The sign-out's full-page load drops them anyway; this says so on purpose.)
  const signOutNow = async () => {
    const run = pending;
    setPending(null);
    if (!run) return;
    await run();
    session?.clearAll();
  };

  const download = async () => {
    if (!session || saving.current) return;
    saving.current = true;
    setBusy(true);
    setError(false);
    try {
      await saveSessionSecureFile(session);
      setSaved(true);
    } catch (err: any) {
      if (err?.name !== "AbortError") setError(true);
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };

  const count = session?.unsavedCount ?? 0;
  const value = useMemo(() => guard, [guard]);

  return (
    <GuardContext.Provider value={value}>
      {children}
      <AlertDialog open={pending !== null} onOpenChange={(open) => { if (!open) close(); }}>
        <AlertDialogContent data-testid="logout-save-check">
          <AlertDialogHeader>
            <AlertDialogTitle>{saved ? LOGOUT_SAVE_CHECK.savedTitle : LOGOUT_SAVE_CHECK.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {saved
                ? "Everything you entered is in your secure file. You can log out now."
                : <>You have {count} sensitive {count === 1 ? "field" : "fields"} that {count === 1 ? "isn't" : "aren't"} in your
                  secure file yet. {count === 1 ? "It is" : "They are"} kept only in this browser, never on our servers, so logging
                  out now loses {count === 1 ? "it" : "them"}.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && (
            <p className="text-sm text-destructive" role="alert" data-testid="logout-save-check-error">
              Couldn't save your secure file. Please try again.
            </p>
          )}
          <AlertDialogFooter>
            {saved ? (
              <Button onClick={() => void signOutNow()} data-testid="logout-save-check-logout">{LOGOUT_SAVE_CHECK.logOut}</Button>
            ) : (
              <>
                <Button variant="outline" onClick={close} disabled={busy} data-testid="logout-save-check-cancel">
                  {LOGOUT_SAVE_CHECK.cancel}
                </Button>
                <Button variant="outline" onClick={() => void signOutNow()} disabled={busy} data-testid="logout-save-check-anyway">
                  {LOGOUT_SAVE_CHECK.logOutAnyway}
                </Button>
                <Button onClick={() => void download()} disabled={busy} data-testid="logout-save-check-download">
                  {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                  {LOGOUT_SAVE_CHECK.download}
                </Button>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </GuardContext.Provider>
  );
}

/**
 * The one way to sign out. Pass the sign-out to run; it runs at once when nothing is unsaved, otherwise after the
 * planner answers the check. Outside `LogoutSaveCheckProvider` (a component rendered alone in a test) it signs out
 * directly — the app mounts the provider at its root, above every sign-out control.
 */
export function useGuardedSignOut(): (signOut: SignOut) => void {
  const guard = useContext(GuardContext);
  return guard ?? ((signOut: SignOut) => { void signOut(); });
}
